/**
 * Returns a boolean indicating whether the provided candidate contains only valid
 * characters for a phone number.
 *
 * @param phoneNumber The phone number to validate
 * @returns {Boolean} `true` if the input only contains valid characters.
 */
export function containsOnlyValidPhoneNumberChars(phoneNumber: string) {
  const phoneNumberIsValidFor = (regex: RegExp) => regex.test(phoneNumber)

  // An optional "+" is allowed only at the start of the string.
  if (!phoneNumberIsValidFor(/^([+]?)[^+]+$/)) {
    return false
  }

  // At most one set of parentheses, and they must be ordered and closed.
  if (
    !phoneNumberIsValidFor(
      /^(?:[+-.0-9/ ]*|[+-.0-9/ ]*\([+-.0-9/ ]*\)[+-.0-9/ ]*)$/,
    )
  ) {
    return false
  }

  // At least 3 numbers must exist.
  if (!phoneNumberIsValidFor(/^(?:[^0-9]*[0-9]){3}.*/)) {
    return false
  }

  // Only integers (0-9) and non-consecutive ( ) + - / ' ' (whitespace) are allowed.
  return phoneNumberIsValidFor(/^(?!.*([()+-/. ])\1)[0-9()+-/. ]+$/)
}

/**
 * Returns a formatted US phone number if it applies or the raw received number
 * if it doesn't match the formatter.
 *
 * @param number The phone number to validate
 */
export function formatUSPhoneNumber(number: string) {
  let parsedNumber = number.replace(/\D/g, '')

  if (parsedNumber.length === 11 && parsedNumber.startsWith('1')) {
    parsedNumber = parsedNumber.substring(1)
  }

  const match = /^(\d{3})(\d{3})(\d{4})$/.exec(parsedNumber)

  return match ? `+1 (${match[1]}) ${match[2]}-${match[3]}` : number
}
